import React, {
  useState,
  Fragment,
  useCallback,
  cloneElement,
  useContext,
} from "react";
import {
  Box,
  TableCell,
  TableRow,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Divider,
  Button,
  Hidden,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { AlertTriangle, Check } from "react-feather";
import {
  ChevronDown as ChevronDownIcon,
  ChevronUp as ChevronUpIcon,
  RefreshCcw as RefreshIcon,
  Airplay as AirplayIcon,
} from "react-feather";
import PlantPage from "../../pages/PlantPage";
import EklokPage from "../../pages/EklokPage";
import Create from "./CreatePlant";
import Modal from "../../components/Modal";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { getConnectedStatusString, getFormattedDate } from "../../utils/utils";
import { mobileFrameHiddenProps } from "../../constants";
import { useHistory } from "react-router";
import { useMobileFrame } from "../../containers/mobile-frame/MobileFrameHomeContainer";
import { useTranslation } from "react-i18next";
import { initConnectionLineStatus } from "../../store/eklok-line-status.store";
import { AuthContext } from "../../context/auth/AuthContext";
import { useDispatch } from "react-redux";

const useStyles = makeStyles((theme) => ({
  buttons: {
    display: "flex",
    alignItems: "center",
    "& > *": {
      marginLeft: theme.spacing(0.5),
      marginRight: theme.spacing(0.5),
      [theme.breakpoints.down("xs")]: {
        marginLeft: 0,
      },
    },
  },
  row: {
    transition: "background-color 0.18s ease",
    "&:hover": {
      backgroundColor: "rgba(233, 58, 36, 0.03)",
    },
  },
  connected: {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 12,
    fontSize: "0.78rem",
    fontWeight: 700,
    backgroundColor: "rgba(46, 184, 92, 0.10)",
    color: "#1a7a3a",
    letterSpacing: "0.02em",
  },
  not_connected: {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 12,
    fontSize: "0.78rem",
    fontWeight: 700,
    backgroundColor: "rgba(233, 58, 36, 0.08)",
    color: "#c62828",
    letterSpacing: "0.02em",
  },
  iconButton: {
    transition: "background-color 0.18s ease, color 0.18s ease",
    "&:hover": {
      backgroundColor: "rgba(233, 58, 36, 0.08)",
      color: theme.palette.primary.main,
    },
  },
  card: {
    margin: "12px 0",
    borderRadius: 12,
    boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
    border: "1px solid rgba(0,0,0,0.06)",
  },
  cellText: {
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
  },
}));

const Row = ({
  item,
  headCells,
  onRefreshAction = () => {},
  onConnectDevice = () => {},
}) => {
  const history = useHistory();
  const classes = useStyles();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("xs"));
  const [open, setOpen] = useState(false);
  const [openConnectEklok, setOpenConnectEklok] = useState(false);
  const { token } = useContext(AuthContext);

  const dispatch = useDispatch();

  const setEklokStatusLineConnection = useCallback(
    (eklokId) => {
      dispatch(
        initConnectionLineStatus({
          token: token,
          body: { eklok: eklokId, line_status: true, process: null },
        }),
      );
    },
    [dispatch, token],
  );

  const goToMobileFrame = useMobileFrame();

  const getInfo = useCallback(
    (id) => {
      switch (id) {
        case "connected":
          return (
            item.ekloks.length > 0 && (
              <Typography
                className={
                  item.ekloks[0]?.connected
                    ? classes.connected
                    : classes.not_connected
                }
              >
                {getConnectedStatusString(item.ekloks[0][id])}
              </Typography>
            )
          );
        case "last_mqtt_update":
          return (
            item.ekloks.length > 0 && (
              <Typography>{getFormattedDate(item.ekloks[0][id])}</Typography>
            )
          );
        case "error":
          return (
            item.ekloks.length > 0 && (
              <Typography>
                {item.ekloks[0].alarm_list?.find(
                  (alarm) => alarm.ALARM_STATUS === "ACTIVE",
                ) ? (
                  <AlertTriangle color="red" />
                ) : (
                  <Check color="green" />
                )}{" "}
              </Typography>
            )
          );
        case "name":
        case "country":
        case "city":
          return <Typography>{item[id]}</Typography>;
        case "connect":
          return (
            <div className={classes.buttons}>
              {item.ekloks.length > 0 ? (
                <Tooltip title="Refresh">
                  <IconButton
                    size="small"
                    onClick={() => onRefreshAction([item])}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
              {item.ekloks.length > 0 && item.ekloks[0]?.connected ? (
                <Tooltip title="Connect">
                  <IconButton
                    size="small"
                    onClick={() => {
                      goToMobileFrame(history, item.ekloks[0]?.id);
                      setEklokStatusLineConnection(item.ekloks[0]?.id);
                    }}
                  >
                    <AirplayIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip title="Detail">
                <IconButton size="small" onClick={() => setOpen(!open)}>
                  {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </IconButton>
              </Tooltip>
            </div>
          );
        default:
          return null;
      }
    },
    [
      item,
      open,
      classes.buttons,
      classes.connected,
      classes.not_connected,
      goToMobileFrame,
      history,
      onRefreshAction,
      setEklokStatusLineConnection,
    ],
  );
  const { t } = useTranslation();

  return (
    <>
      <Hidden xsDown>
        <TableRow key={item.id} className={classes.row}>
          <TableCell><Typography className={classes.cellText}>{item.name}</Typography></TableCell>
          <TableCell><Typography className={classes.cellText}>{item.country}</Typography></TableCell>
          <TableCell><Typography className={classes.cellText}>{item.city}</Typography></TableCell>
          <TableCell>
            <Typography className={classes.cellText}>
              {item.ekloks[0] && item.ekloks[0].last_mqtt_update
                ? getFormattedDate(item.ekloks[0].last_mqtt_update)
                : ""}
            </Typography>
          </TableCell>
          {item.ekloks.length > 0 ? (
            <TableCell>
              <span
                className={
                  item.ekloks[0]?.connected
                    ? classes.connected
                    : classes.not_connected
                }
              >
                {getConnectedStatusString(item.ekloks[0]?.connected)}
              </span>
            </TableCell>
          ) : (
            <TableCell><Typography className={classes.cellText}>-</Typography></TableCell>
          )}
          {item.ekloks.length > 0 ? (
            <TableCell>
              {item.ekloks[0]?.alarm_list &&
              item.ekloks[0]?.alarm_list.find(
                (alarm) => alarm.ALARM_STATUS === "ACTIVE",
              ) ? (
                <AlertTriangle color="red" />
              ) : (
                <Check color="green" />
              )}
            </TableCell>
          ) : (
            <TableCell><Typography className={classes.cellText}>-</Typography></TableCell>
          )}

          <TableCell>
            <div className={classes.buttons}>
              {item.ekloks.length > 0 ? (
                <Tooltip title={t("tooltip_titles.refresh")}>
                  <IconButton
                    className={classes.iconButton}
                    size="small"
                    onClick={() => onRefreshAction([item])}
                  >
                    <RefreshIcon size={16} />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Hidden xsDown>
                {item.ekloks.length > 0 && item.ekloks[0]?.connected ? (
                  <Tooltip title={t("tooltip_titles.connect")}>
                    <IconButton
                      className={classes.iconButton}
                      size="small"
                      onClick={() => onConnectDevice(item.ekloks[0])}
                    >
                      <AirplayIcon size={16} />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Hidden>
              <Tooltip title={t("tooltip_titles.details")}>
                <IconButton className={classes.iconButton} size="small" onClick={() => setOpen(!open)}>
                  {open ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
                </IconButton>
              </Tooltip>
            </div>
          </TableCell>
        </TableRow>
      </Hidden>

      {cloneElement(
        <Hidden>
          <Card className={classes.card}>
            <CardContent>
              {headCells?.map((headCell) => (
                <Grid container key={headCell.id}>
                  <Grid item xs={5}>
                    <Typography variant="h5">{headCell.label}</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    {getInfo(headCell.id, item) || "-"}
                  </Grid>
                </Grid>
              ))}
            </CardContent>
          </Card>
        </Hidden>,
        mobileFrameHiddenProps,
      )}

      {open ? (
        <Modal
          fullScreen={matches}
          title={item.name}
          onClose={() => setOpen(false)}
          open={open}
          fullWidth={true}
          maxWidth="lg"
        >
          <PlantPage item={item} />
          <Divider />
          {item.ekloks.length > 0 ? (
            <EklokPage item={item.ekloks[0]} />
          ) : (
            <Box m={2}>
              <Button
                color="primary"
                variant="contained"
                onClick={() => setOpenConnectEklok(true)}
              >
                {t("details_modal.connect_eklok")}
              </Button>
            </Box>
          )}
          {openConnectEklok ? (
            <Create
              handleClose={() => setOpenConnectEklok(false)}
              open={openConnectEklok}
              step={1}
              plantDefault={item}
            />
          ) : null}
        </Modal>
      ) : null}
    </>
  );
};

export default Row;
